import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ContactFormFields, { EMPTY_CONTACT_FORM, ROLES, SENIORITY } from './ContactFormFields';
import { missingRequiredCustomFields } from '../custom-fields/requiredFields';
import useCustomFields from '../../hooks/useCustomFields';
import { isValidEmail } from '../../utils/emailValidation';
import { normalizeContactPayload } from '../../utils/normalizeContactPayload';
import { errorText, contactExists } from '../../lib/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// The field block is `ContactFormFields` (#1013): the Guests tab's Add guests dialog renders the same one inline.
export { ROLES, SENIORITY };

/**
 * The New / Edit Contact form, shared by the Contacts page and the event's "Add Contacts" dialog (#919, D10: a contact
 * created from an event page opens this same form in place, and the caller binds the saved contact to the event).
 * `onSave(contact)` runs with the saved record just before `onClose()`. `initial` pre-fills a NEW contact's fields (read when
 * the form opens, never while it is being typed in).
 */
export default function ContactModal({ open, onClose, contact, onSave, initial = null }) {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    // #902: the existing contact that already holds the typed email.
    const [exists, setExists] = useState(null);
    const { fields: customFieldDefs } = useCustomFields('contact');
    const [form, setForm] = useState(EMPTY_CONTACT_FORM);
    const [loading, setLoading] = useState(false);
    // Set by a Save that a Required custom field blocked; the inline errors then follow the values.
    const [showRequired, setShowRequired] = useState(false);
    // Linked-company record for the picker trigger label (so the right
    // company shows even when it is absent from the current search results).
    const [linkedAccount, setLinkedAccount] = useState(null);
    const initialRef = useRef(initial);
    initialRef.current = initial;

    useEffect(() => {
        if (contact) {
            setForm({ ...contact, custom_fields: contact.custom_fields || {} });
            if (contact.account_id) {
                axios.get(`${API}/accounts/${contact.account_id}`, { headers: getAuthHeader() })
                    .then(res => setLinkedAccount(res.data))
                    .catch(() => setLinkedAccount(null));
            } else {
                setLinkedAccount(null);
            }
        } else {
            setForm({ ...EMPTY_CONTACT_FORM, ...(initialRef.current || {}) });
            setLinkedAccount(null);
        }
        setExists(null);
        setShowRequired(false);
    }, [contact, open, getAuthHeader]);

    const handleSave = async () => {
        if (!form.first_name || !form.last_name) return toast.error('First and last name required');
        if (!form.email?.trim()) return toast.error('Email is required');
        if (!isValidEmail(form.email)) return toast.error('Please enter a valid email address');
        // D4 (#972): a Required custom field must be filled in the app.
        if (Object.keys(missingRequiredCustomFields(customFieldDefs, form.custom_fields)).length) {
            setShowRequired(true);
            return toast.error('Fill in the required custom fields');
        }
        setLoading(true);
        try {
            let res;
            const payload = normalizeContactPayload(form);
            if (contact) res = await axios.put(`${API}/contacts/${contact.contact_id}`, payload, { headers: getAuthHeader() });
            else res = await axios.post(`${API}/contacts`, payload, { headers: getAuthHeader() });
            toast.success(contact ? 'Contact updated' : 'Contact created');
            onSave(res.data);
            onClose();
        } catch (e) {
            const dup = contactExists(e);
            if (dup) setExists(dup);
            else toast.error(errorText(e, 'Failed to save contact'));
        }
        finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{contact ? 'Edit Contact' : 'New Contact'}</DialogTitle>
                </DialogHeader>
                <ContactFormFields
                    key={contact?.contact_id || 'new'}
                    form={form}
                    setForm={setForm}
                    onEmailChange={() => setExists(null)}
                    customFieldDefs={customFieldDefs}
                    showRequired={showRequired}
                    linkedAccount={linkedAccount}
                    setLinkedAccount={setLinkedAccount}
                />
                {exists && (
                    <div role="alert" data-testid="contact-exists-notice"
                        className="flex gap-3 justify-between items-center px-3 py-2 mt-4 text-sm text-amber-900 bg-amber-50 rounded-lg border border-amber-200">
                        <span>{exists.message}</span>
                        {exists.id && (
                            <button type="button" data-testid="contact-exists-open"
                                onClick={() => { onClose(); navigate(`/contacts/${exists.id}`); }}
                                className="font-semibold underline shrink-0 hover:no-underline">Open contact</button>
                        )}
                    </div>
                )}
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} data-testid="contact-save-btn"
                        className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save Contact'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
